import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleHelp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

export function QueueScheduleHelp() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const key = 'publishQueues.form.scheduleHelp';

  return (
    <>
      <Button ref={triggerRef} type="button" variant="ghost" size="icon"
        className="h-8 w-8 shrink-0 rounded-full text-muted-foreground hover:text-foreground"
        aria-label={t(`${key}.title`)} title={t(`${key}.title`)}
        aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
        <CircleHelp className="h-4 w-4" aria-hidden="true" />
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90dvh] w-[calc(100%_-_2rem)] max-w-xl grid-cols-[minmax(0,1fr)] overflow-y-auto"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            titleRef.current?.focus({ preventScroll: true });
          }}
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            triggerRef.current?.focus({ preventScroll: true });
          }}>
          <DialogHeader className="pr-5 text-left">
            <DialogTitle ref={titleRef} tabIndex={-1} className="leading-snug outline-none">{t(`${key}.title`)}</DialogTitle>
            <DialogDescription>{t(`${key}.description`)}</DialogDescription>
          </DialogHeader>
          <ol className="list-decimal space-y-3 pl-5 text-sm leading-relaxed text-muted-foreground marker:font-semibold marker:text-foreground">
            {(['selection', 'spacing', 'overflow'] as const).map((step) => (
              <li key={step} className="pl-1">{t(`${key}.${step}`)}</li>
            ))}
          </ol>
          <p className="text-sm leading-relaxed text-muted-foreground">{t(`${key}.capacity`)}</p>
          <div className="min-w-0 rounded-xl border bg-card p-3 sm:p-4">
            <h3 className="text-sm font-semibold">{t(`${key}.exampleTitle`)}</h3>
            <p className="mb-2 mt-1 text-xs leading-relaxed text-muted-foreground">{t(`${key}.exampleDescription`)}</p>
            <Table aria-label={t(`${key}.exampleTitle`)} className="table-fixed">
              <TableHeader>
                <TableRow>
                  <TableHead scope="col" className="h-auto w-2/5 px-2 py-3 text-xs">{t(`${key}.previous`)}</TableHead>
                  <TableHead scope="col" className="h-auto px-2 py-3 text-xs">{t(`${key}.next`)}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[
                  ['10:10', '11:10–11:20'],
                  ['10:30', '11:30–11:55'],
                  ['11:10', t(`${key}.nextWindow`)],
                ].map(([previous, next]) => (
                  <TableRow key={previous}>
                    <TableCell className="px-2 py-2.5 align-top tabular-nums">{previous}</TableCell>
                    <TableCell className="px-2 py-2.5 tabular-nums">{next}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <DialogFooter>
            <Button type="button" onClick={() => setOpen(false)}>{t(`${key}.close`)}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
